import type { StreamPoint } from './workoutStream';

export type RunningStat = { min?: number | null; avg?: number | null; max?: number | null };
export interface RunningMetrics {
  version: string;
  cadence_unit: 'spm';
  raw_cadence_unit: 'cycles/min';
  distance_m?: number | null;
  duration_seconds?: number;
  calories?: number;
  ascent_m?: number;
  descent_m?: number;
  avg_pace_seconds_per_km?: number | null;
  equivalent_pace_seconds_per_km?: number | null;
  cadence?: RunningStat | null;
  stride_length?: RunningStat;
  stride_length_estimated?: boolean;
  vertical_oscillation?: RunningStat;
  ground_contact_time?: RunningStat;
  ground_contact_balance?: RunningStat;
  vertical_ratio?: RunningStat;
  respiration_rate?: RunningStat;
  heart_rate?: RunningStat;
  power?: RunningStat;
  temperature?: RunningStat;
  steps?: number;
  aerobic_training_effect?: number;
  anaerobic_training_effect?: number;
  rtss?: number;
  intensity_factor?: number;
  load?: { threshold_value?: number; grade_adjusted?: boolean; estimated?: boolean; algorithm?: string };
}

export type RunningWorkout = {
  fit_sport?: number | null;
  source?: string | null;
  workout_type?: { id?: number; english_name?: string | null; name?: string | null } | null;
  running_metrics?: RunningMetrics | null;
};

export function isRunningWorkout(workout: RunningWorkout): boolean {
  if (workout.fit_sport != null) return workout.fit_sport === 1;
  const type = workout.workout_type;
  return type?.id === 1 || /^(run|running|trail run|trail running|road running|track running)$/i.test((type?.english_name ?? type?.name ?? '').trim());
}

export function formatRunningPace(seconds: unknown): string {
  if (seconds == null || !Number.isFinite(Number(seconds)) || Number(seconds) <= 0) return '—';
  const rounded = Math.round(Number(seconds));
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, '0')}`;
}

export function paceFromKmh(speed: unknown): string {
  return speed != null && Number(speed) > 0 ? formatRunningPace(3600 / Number(speed)) : '—';
}

/** Raw API channels are never mutated; the display copy explicitly marks its unit. */
export function runningDisplayStream(stream: StreamPoint[], workout: RunningWorkout): StreamPoint[] {
  if (!isRunningWorkout(workout)) return stream;
  const hasFitCycles = workout.running_metrics?.raw_cadence_unit === 'cycles/min'
    || ['fit', 'garmin_fit', 'tcx', 'wahoo'].includes(workout.source ?? '');
  if (!hasFitCycles) return stream;
  return stream.map(point => point.cadenceUnit === 'spm' ? point : ({
    ...point,
    cadence: point.cadence == null ? null : (point.cadence + (point.fractionalCadence ?? 0)) * 2,
    cadenceUnit: 'spm',
  }));
}
